import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import React from 'react';
import { BotResponseRating, CSATRating } from '../../enums/ChannelStatus';
import Modal from '../Modal/Modal';
import FormTextArea from '../TextArea';
import { Label, PanelTitle } from '../Typography';
import { Button, CSATSurveyContainer, FlexContainer, SkipButton, SubmitButton } from './styled';

type CSATSurveyType = {
  botEnabled: boolean;
  onSkip: VoidFunction;
  onSubmit: (surveyOptions) => any;
  surveyError: null | string;
};

export const TITLE = 'Your chat has ended. Thanks for reaching out to us.';
export const CHAT_LABEL = 'How was your chat experience overall?';
export const AI_LABEL = 'How was our AI Assistant?';
export const SUBMIT = 'Submit';
export const SKIP = 'Skip';
export const PLACEHOLDER = 'Tell us how we could improve (optional)';

function CSATSurvey({ botEnabled, onSkip, onSubmit, surveyError }: CSATSurveyType) {
  const [aiXpScore, setAiXpScore] = React.useState<BotResponseRating>(BotResponseRating.None);
  const [chatXpScore, setChatXpScore] = React.useState<CSATRating>(CSATRating.None);
  const [feedback, setFeedback] = React.useState<string>('');
  const showTextArea = aiXpScore === BotResponseRating.Down || chatXpScore === CSATRating.Down;

  return (
    <Modal>
      <CSATSurveyContainer>
        <PanelTitle>{TITLE}</PanelTitle>
        <FlexContainer style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Label>{CHAT_LABEL}</Label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              filled={chatXpScore === CSATRating.Up}
              onClick={() => {
                setChatXpScore(CSATRating.Up);
              }}
              data-testid='chat-thumbs-up'
            >
              <ThumbUpIcon />
            </Button>
            <Button
              filled={chatXpScore === CSATRating.Down}
              onClick={() => {
                setChatXpScore(CSATRating.Down);
              }}
              data-testid='chat-thumbs-down'
            >
              <ThumbDownIcon />
            </Button>
          </div>
        </FlexContainer>
        {botEnabled && (
          <FlexContainer spaceBetween style={{ alignItems: 'center' }}>
            <Label>{AI_LABEL}</Label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                filled={aiXpScore === BotResponseRating.Up}
                onClick={() => {
                  setAiXpScore(BotResponseRating.Up);
                }}
                data-testid='ai-thumbs-up'
              >
                <ThumbUpIcon aria-label='thumbs-up' />
              </Button>
              <Button
                filled={aiXpScore === BotResponseRating.Down}
                onClick={() => {
                  setAiXpScore(BotResponseRating.Down);
                }}
                data-testid='ai-thumbs-down'
              >
                <ThumbDownIcon aria-label='thumbs-down' />
              </Button>
            </div>
          </FlexContainer>
        )}
        {showTextArea && (
          <>
            <FlexContainer flexCol gap={2}>
              <FormTextArea onChange={(e) => setFeedback(e.target.value)} placeholder={PLACEHOLDER} value={feedback} />
              <span style={{ alignSelf: 'flex-end' }}>{feedback.length}/500</span>
            </FlexContainer>
          </>
        )}
        {surveyError && <p style={{ color: '#FF0000', fontSize: 12 }}>{surveyError}</p>}
        <FlexContainer>
          <SkipButton onClick={onSkip}>{SKIP}</SkipButton>
          <SubmitButton
            data-testid='submit-survey'
            disabled={(botEnabled && aiXpScore === BotResponseRating.None) || chatXpScore === CSATRating.None}
            filled
            onClick={() =>
              onSubmit({
                comment: feedback,
                overallRating: chatXpScore,
                ...(botEnabled ? { botRating: aiXpScore } : {}),
              })
            }
          >
            {SUBMIT}
          </SubmitButton>
        </FlexContainer>
      </CSATSurveyContainer>
    </Modal>
  );
}

export default CSATSurvey;
